import _ from "lodash";
import React, { useCallback, useEffect, useState, memo } from "react";
import UIModal from "UIComponents/UIModal";

import Geogebra from "./Geogebra/index";
import { CornersOutOutlined, PencilLineOutlined } from "@toddle-edu/ds-icons";
import { Button } from "@toddle-edu/ds-web";
import classes from "./Graph.scss";
import { generateRandomId } from "Utils";
import { uploadFileV2 } from "modules/Services";
import { connect } from "react-redux";
import { useI18n } from "Hooks";
import Loading from "UIComponents/Loading";

import axiosInstance from "lib/axiosInstance";
import Header from "./Header";
import {
  DEFAULT_GRAPH_IMAGE_URL,
  CLASSIC_TOOLBAR_CONFIG_STR,
  GEOMETRIC_TOOLBAR_CONFIG_STR,
} from "./GraphConstants";
import {
  updateIsGraphTheaterModeEnabled,
  updateIsQuestionTheaterModeEnable,
  updateLoadingState,
} from "Worksheet/modules/WorksheetModules";

const styles = {
  loadingContainerStyle: {
    height: "100%",
  },
};

const Graph = memo(props => {
  const {
    mode,
    value,
    uploadFileV2,
    updateGraphData,
    config,
    updateIsGraphTheaterModeEnabled,
    testId = "",
    enableAutoSubmit,
    updateIsQuestionTheaterModeEnable,
    updateLoadingState,
    showGraphImage = true, // pass this prop to hide the default graph image
    onClose = _.noop,
    worksheetMode,
    isFromInternalTool,
  } = props;
  const {
    showEditGraphOption,
    fixGraphOnSave,
    disableFixedGraph,
    allowAlgebraView,
    hideTitleBarPanel,
  } = config;
  const [capturedImage, setCapturedImage] = useState(null);

  const [xml, setXml] = useState(null);
  const [type, setType] = useState("classic");
  const [enableGraph, setEnableGraph] = useState(!showGraphImage);

  const [imageLoaded, setImageLoaded] = useState(false);
  const { t } = useI18n(["common", "worksheet"]);

  const handleIsGraphEnable = visibilityFlag => {
    setEnableGraph(visibilityFlag);
    updateIsGraphTheaterModeEnabled(visibilityFlag);
    onClose();
  };

  const handleImageLoad = () => {
    setImageLoaded(true);
  };

  const uploadImage = async blob => {
    const id = generateRandomId();
    const graphImageName = `GRAPH-THUMB-${id}`;

    const fileObj = new File([blob], graphImageName, {
      type: "image/png",
    });

    const url = await uploadFileV2({
      file: fileObj,
      attachment: {
        name: graphImageName,
        type: "IMAGE",
        mimeType: "image/png",
        metadata: {
          fileExtension: "png",
        },
      },
      uploadId: id,
    });
    return url;
  };

  const uploadXML = async xml => {
    const id = generateRandomId();

    const graphXMLName = `GRAPH-XML-${id}`;
    const fileObj = new File([xml], graphXMLName, {
      type: "text/xml",
    });

    const url = await uploadFileV2({
      hideToastProgress: true,
      file: fileObj,
      attachment: {
        name: graphXMLName,
        type: "XML",
        mimeType: "text/xml",
        metadata: {
          fileExtension: "xml",
        },
      },
      uploadId: id,
    });
    return url;
  };

  const handleSaveGraph = async () => {
    const canvas = document.querySelector('[data-test="euclidianView"]');
    if (canvas) {
      const blob = await new Promise(resolve => {
        canvas.toBlob(blob => resolve(blob), "image/png");
      });

      if (fixGraphOnSave) {
        const names = window.ggbApplet.getAllObjectNames();
        for (let i = 0; i < names.length; i++) {
          window.ggbApplet.setFixed(names[i], true, false);
          window.ggbApplet.setAuxiliary(names[i], true);
        }
      }
      const xmlData = window.ggbApplet.getXML();

      const [dataUrl, thumbUrl] = await Promise.all([
        uploadXML(xmlData),
        uploadImage(blob),
      ]);

      updateGraphData(dataUrl, thumbUrl, type);

      setCapturedImage(URL.createObjectURL(blob));
      setXml(xmlData);
      handleIsGraphEnable(false);
    }
  };

  const handleSaveBtnClick = async () => {
    updateLoadingState(true);
    await handleSaveGraph();
    updateIsQuestionTheaterModeEnable(false);
    updateLoadingState(false);
  };
  const [undoState, setUndoState] = useState(0);
  const [redoState, setRedoState] = useState(0);

  const onUndo = () => {
    const app = window.ggbApplet;

    setUndoState(prev => prev - 1);
    setRedoState(prev => prev + 1);

    app.undo();
  };

  const onRedo = () => {
    const app = window.ggbApplet;

    setRedoState(prev => prev - 1);
    setUndoState(prev => prev + 1);

    app.redo();
  };

  useEffect(() => {
    if (enableAutoSubmit && enableGraph) handleSaveBtnClick();
  }, [enableAutoSubmit]);

  const updateGraphType = optionValue => {
    setType(optionValue.key);
  };

  const undoListener = () => {
    setUndoState(prev => prev + 1);
  };

  const onAppletLoad = useCallback(() => {
    const app = window.ggbApplet;
    const toolbar = document.querySelector(".toolbarPanel");
    const sideBarMenu = document.querySelector(".withMenu");
    if (type !== "classic") app.setPerspective("-A");

    if (toolbar) {
      toolbar.style.zIndex = "61";
    }
    if (xml) {
      app.setXML(xml);
      app.setUndoPoint();

      app?.registerStoreUndoListener(undoListener);
    }
    app.showMenuBar(false);

    if (!showEditGraphOption) {
      const names = app.getAllObjectNames();
      for (let i = 0; i < names.length; i++) {
        app.setFixed(names[i], true, false);
        app.setAuxiliary(names[i], true);
      }
    }
    if (!_.includes(["classic"], type)) {
      const landscapeMenuBtn = document.querySelector(".landscapeMenuBtn");
      if (landscapeMenuBtn) landscapeMenuBtn.style.display = "none";
      if (sideBarMenu) sideBarMenu.style.top = "8px";
    }
    if (disableFixedGraph) {
      const names = app.getAllObjectNames();
      for (let i = 0; i < names.length; i++) {
        app.setFixed(names[i], false, true);
        app.setAuxiliary(names[i], false);
      }
    }
    if (!allowAlgebraView) {
      app.setPerspective("-A");
      app.showAlgebraInput(false);
      if (!_.includes(["classic"], type)) {
        if (sideBarMenu) sideBarMenu.childNodes[0].style.display = "none";
      }
    }
    if (hideTitleBarPanel) {
      if (_.includes(["classic"], type)) {
        const titleBars = document.querySelectorAll(".TitleBarPanel");
        _.forEach(titleBars, function (titleBar) {
          titleBar.style.display = "none";
        });
      } else {
        const graphContextMenu = document.querySelector(
          ".graphicsControlsPanel"
        );
        if (graphContextMenu) graphContextMenu.style.display = "none";
      }
    }
  }, [
    type,
    xml,
    showEditGraphOption,
    disableFixedGraph,
    allowAlgebraView,
    hideTitleBarPanel,
  ]);
  useEffect(() => {
    const fetchGraphData = async () => {
      try {
        if (value) {
          const response = await axiosInstance.get(value?.url);

          setType(value?.type);
          setXml(response.data);
          setCapturedImage(value?.thumbUrl);
        } else {
          setCapturedImage(DEFAULT_GRAPH_IMAGE_URL);
        }
      } catch (error) {}
    };

    fetchGraphData();
    return () => {
      const app = window.ggbApplet;
      app?.unregisterStoreUndoListener(undoListener);
    };
  }, []);

  const onGraphOpen = () => {
    if (showEditGraphOption) updateIsQuestionTheaterModeEnable(true);
    handleIsGraphEnable(true);
  };
  const onGraphClose = () => {
    if (showEditGraphOption) updateIsQuestionTheaterModeEnable(false);
    handleIsGraphEnable(true);
  };
  let customToolBar = "";
  switch (type) {
    case "classic":
      customToolBar = CLASSIC_TOOLBAR_CONFIG_STR;
      break;
    case "geometry":
      customToolBar = GEOMETRIC_TOOLBAR_CONFIG_STR;
      break;
    default:
      customToolBar = "";
  }

  return (
    <div className={classes.graphContainer}>
      {showGraphImage ? (
        <div className={classes.graphImage}>
          {capturedImage ? (
            <img
              src={capturedImage}
              alt="Captured"
              onLoad={handleImageLoad}
              className={classes.capturedImage}
            />
          ) : null}
          {!imageLoaded && !capturedImage && (
            <div className={classes.loadingContainer}>
              <Loading />
            </div>
          )}

          {imageLoaded && capturedImage ? (
            <div className={classes.overlay}>
              <Button
                size={"medium"}
                variant={"progressive"}
                onClick={onGraphOpen}
                icon={
                  showEditGraphOption ? (
                    <PencilLineOutlined />
                  ) : (
                    <CornersOutOutlined />
                  )
                }
                testId={`${testId}-graph-openGraph`}
              >
                {showEditGraphOption ? t("common:edit") : t("common:view")}
              </Button>
            </div>
          ) : null}
        </div>
      ) : null}

      {enableGraph ? (
        <UIModal
          isOpen={true}
          isFullScreen={true}
          onRequestClose={onGraphClose}
        >
          <Header
            updateGraphType={updateGraphType}
            handleSaveGraph={handleSaveGraph}
            handleIsGraphEnable={handleIsGraphEnable}
            type={type}
            mode={mode}
            config={config}
            value={value}
            testId={testId}
            redoState={redoState}
            undoState={undoState}
            onUndo={onUndo}
            onRedo={onRedo}
            worksheetMode={worksheetMode}
            isFromInternalTool={isFromInternalTool}
          />
          <div className={classes.graphContainer}>
            {!showGraphImage && !imageLoaded && !capturedImage ? (
              <Loading containerStyle={styles.loadingContainerStyle} />
            ) : (
              <Geogebra
                appName={type}
                borderColor="null"
                showAlgebraInput={allowAlgebraView ? "true" : "false"}
                enableLabelDrags="false"
                enableShiftDragZoom="true"
                capturingThreshold="null"
                showToolBarHelp="false"
                errorDialogsActive="true"
                allowStyleBar={hideTitleBarPanel ? "false" : "true"}
                showMenuBar={showEditGraphOption ? "true" : "false"}
                appletOnLoad={onAppletLoad}
                showKeyboardOnFocus="false"
                customToolBar={customToolBar}
                enableUndoRedo="false"
              />
            )}
          </div>
        </UIModal>
      ) : null}
    </div>
  );
});

Graph.displayName = "Graph";

const mapActionCreators = {
  uploadFileV2,
  updateIsGraphTheaterModeEnabled,
  updateIsQuestionTheaterModeEnable,
  updateLoadingState,
};

const mapStateToProps = state => {
  const enableAutoSubmit = _.get(state, "worksheet.enableAutoSubmit", false);
  return {
    enableAutoSubmit,
  };
};

export default connect(mapStateToProps, mapActionCreators)(Graph);
